/**
 * =============================================================================
 * ADMIN API CLIENT
 * =============================================================================
 *
 * API client for admin dashboard communication with the Worker backend.
 * Uses the BigCommerce signed_payload_jwt for authentication.
 *
 * ARCHITECTURE REFERENCE: Section 6 - API Specification
 * PHASE_0B_SPEC.md: Task 2.4.2
 *
 * =============================================================================
 */

import type {
  ApiResponse,
  AdminQuestionListResponse,
  AdminQuestionDetailResponse,
  AdminQuestionFilters,
  AdminReplyListResponse,
  AdminReplyFilters,
  ModerateReplyRequest,
  ModerateReplyResponse,
  ReplyModerationAction,
  CreateStaffReplyRequest,
  CreateStaffReplyResponse,
  UpdateQuestionResponse,
  PendingNotificationsResponse,
  AnalyticsResponse,
  TagSuggestionResponse,
  AdminTagTaxonomyResponse,
  AdminReputationListResponse,
  LeaderboardResponse,
  StoreSettingsResponse,
  UpdateStoreSettingsRequest,
  AdminLaunchStatusResponse,
  ProductSearchResponse,
  ProductsByIdsResponse,
  ParticipantSupportLookupResponse,
  ParticipantResubscribeResponse,
  ConsentInspectionResponse,
  AdminRfiRequest,
  AdminRfiListResponse,
  CreateRfiRequest,
  CreateRfiResponse,
  CreateRfiResponseLinkInput,
  RfiResponseLinkTarget,
  ReviewRfiItemInput,
  ReviewRfiItemResponse,
  CancelRfiRequest,
  CancelRfiResponse,
  AuditLogListQuery,
  AuditLogListResponse,
  AdminMediaFilters,
  AdminMediaListResponse,
  ModerateMediaRequest,
  ModerateMediaResponse,
  BulkModerateMediaRequest,
  BulkModerateMediaResponse,
  MediaModerationAction,
  MediaRejectionReason,
  QuestionRejectionReason,
  QuestionStatus,
  MarkSolutionRequest,
  AdminUploadImageResponse,
  AdminDeleteMediaResponse,
} from '@find-my-part/types';

import { fetchWithRetry, MaxRetriesExceededError, RequestTimeoutError } from './fetchWithRetry';
import { DEFAULT_REQUEST_TIMEOUT_MS } from './constants';

// =============================================================================
// CONFIGURATION
// =============================================================================

/**
 * Base URL for API requests.
 * In development, this defaults to the local Worker.
 * In production, this should point to the deployed Worker URL.
 */
export const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

/**
 * Default request timeout in milliseconds (30 seconds).
 *
 * Re-exported from `./constants` for backward compatibility with any
 * callers that already import it from here. The canonical home is
 * `./constants` because `./fetchWithRetry` also needs the value, and
 * importing it from this file used to create a module-init cycle.
 *
 * P1-11: AbortController for API Request Timeouts
 */
export { DEFAULT_REQUEST_TIMEOUT_MS };

/**
 * Converts a relative API URL to an absolute URL.
 *
 * The backend returns relative URLs like `/api/admin/images/{attachmentId}`.
 * When the admin UI is embedded in BigCommerce, these relative URLs resolve
 * against the wrong origin. This helper ensures images load from the correct
 * Worker API origin.
 *
 * @param relativeUrl - A relative URL starting with '/'
 * @returns Absolute URL with API_BASE prepended
 *
 * @example
 * ```ts
 * // Returns 'https://worker.example.com/api/admin/images/att_xyz'
 * getAbsoluteApiUrl('/api/admin/images/att_xyz');
 * ```
 */
export function getAbsoluteApiUrl(relativeUrl: string): string {
  // If already absolute, return as-is
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl;
  }
  // Ensure no double slashes when joining
  const base = API_BASE.endsWith('/') ? API_BASE.slice(0, -1) : API_BASE;
  const path = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;
  return `${base}${path}`;
}

// =============================================================================
// HTTP CLIENT
// =============================================================================

/**
 * Options for API requests.
 */
interface ApiRequestOptions extends Omit<RequestInit, 'headers'> {
  headers?: Record<string, string>;
  /** Optional timeout in milliseconds. Defaults to DEFAULT_REQUEST_TIMEOUT_MS (30s). */
  timeoutMs?: number;
  /** Optional max retry attempts. Defaults to 3. P1-12 */
  maxRetries?: number;
  /** Preserve the final retryable HTTP response body for structured recovery. */
  preserveFinalRetryableResponse?: boolean;
  /**
   * Optional caller `AbortController` signal for request cancellation. Merged
   * with the internal per-attempt timeout signal by `fetchWithRetry`; a
   * caller-initiated abort propagates as a real `AbortError` (not a timeout) so
   * callers can detect and silently ignore a superseded request. (PR I-2 — the
   * first admin-ui consumer is the audit viewer's `useAudit` hook.)
   */
  signal?: AbortSignal;
}

/**
 * Makes an authenticated API request with automatic timeout and retry.
 *
 * P1-11: Implements AbortController-based timeout with user-friendly error messages.
 * P1-12: Implements retry logic with exponential backoff for transient failures.
 *
 * FormData-aware (PR4b fold R1): a `FormData` body (staff multipart upload)
 * gets NO manual `Content-Type` header — the browser must set
 * `multipart/form-data; boundary=…` itself, since a manual header would omit
 * the boundary and the worker's `formData()` parse would 400. Every other
 * request keeps the JSON `Content-Type` default. Multipart callers also pass
 * `maxRetries: 0` — an upload POST is non-idempotent from the client
 * (rfi.cancel precedent): a retry after a silently-committed first attempt
 * would consume a SECOND slot of the per-target image cap (and D-QUOTA
 * bytes) for one operator action.
 *
 * @param token - The BigCommerce signed_payload_jwt
 * @param endpoint - The API endpoint path (e.g., '/api/admin/questions')
 * @param options - Additional fetch options including optional timeout and retry overrides
 * @returns The API response
 * @throws {DOMException} A real `AbortError` when a caller-supplied `signal`
 *   aborts the request (a superseded/cancelled call — distinct from an internal
 *   per-attempt timeout, which is returned as a `REQUEST_TIMEOUT` envelope). All
 *   other failures resolve to an error envelope; only a caller abort rejects.
 *
 * @example
 * ```ts
 * const response = await apiRequest<AdminQuestionListResponse>(
 *   token,
 *   '/api/admin/questions'
 * );
 * ```
 */
async function apiRequest<T>(
  token: string,
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse<T>> {
  const {
    headers,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
    maxRetries = 3,
    preserveFinalRetryableResponse = false,
    ...restOptions
  } = options;

  // P2-27: Generate per-request correlation ID for distributed tracing
  const correlationId = crypto.randomUUID();

  // R1: never set Content-Type on a FormData body — the browser must supply
  // the multipart boundary itself (see the function doc).
  const isFormDataBody = restOptions.body instanceof FormData;

  try {
    // P1-12: Use fetchWithRetry for automatic retry on transient failures
    const response = await fetchWithRetry(
      `${API_BASE}${endpoint}`,
      {
        ...restOptions,
        headers: {
          ...(isFormDataBody ? {} : { 'Content-Type': 'application/json' }),
          Authorization: `Bearer ${token}`,
          'X-Correlation-ID': correlationId,
          ...headers,
        },
      },
      { timeoutMs, maxRetries, preserveFinalRetryableResponse }
    );

    // Handle non-JSON responses gracefully (4xx errors that weren't retried)
    if (!response.ok) {
      try {
        return await response.json();
      } catch (bodyError) {
        // A caller abort can also fire while the error body is being read —
        // it must still reject as an AbortError (the documented @throws
        // contract), not degrade into an envelope. The outer catch rethrows it.
        if (bodyError instanceof Error && bodyError.name === 'AbortError') {
          throw bodyError;
        }
        return {
          success: false,
          error: {
            code: 'NETWORK_ERROR',
            message: `${response.status} ${response.statusText}`,
          },
        };
      }
    }

    return response.json();
  } catch (error) {
    // PR I-2: Propagate a caller-initiated cancel (a real AbortError, distinct
    // from a timeout — see fetchWithRetry) so the caller can detect and silently
    // ignore a superseded request rather than surfacing a spurious error envelope.
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }

    // P1-11: Handle timeout
    if (error instanceof RequestTimeoutError) {
      return {
        success: false,
        error: {
          code: 'REQUEST_TIMEOUT',
          message: error.message,
        },
      };
    }

    // P1-12: Handle max retries exceeded
    if (error instanceof MaxRetriesExceededError) {
      return {
        success: false,
        error: {
          code: 'MAX_RETRIES_EXCEEDED',
          message: 'The server is temporarily unavailable. Please try again later.',
        },
      };
    }

    // Network errors (fetch itself fails)
    return {
      success: false,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Request failed',
      },
    };
  }
}

// =============================================================================
// RE-EXPORT TYPES FOR CONSUMERS
// =============================================================================

/**
 * Re-export AdminQuestionFilters for API consumers.
 * This provides a convenient import path: `import { AdminQuestionFilters } from './api/client'`
 */
export type { AdminQuestionFilters } from '@find-my-part/types';

// =============================================================================
// ADMIN API CLIENT
// =============================================================================

/**
 * Admin API client for question management.
 *
 * This object provides methods for interacting with admin API endpoints.
 * Each method requires a valid BigCommerce JWT token for authentication.
 *
 * @example
 * ```tsx
 * function QuestionQueue() {
 *   const { token } = useSession();
 *
 *   useEffect(() => {
 *     if (token) {
 *       adminApi.questions.list(token).then(response => {
 *         if (response.success) {
 *           setQuestions(response.data.questions);
 *         }
 *       });
 *     }
 *   }, [token]);
 * }
 * ```
 */
/**
 * Fetches an admin image with authentication.
 *
 * HTML <img> tags cannot send custom Authorization headers, so we must use
 * fetch() to retrieve authenticated images. This function returns a blob URL
 * that can be used as an <img src> attribute.
 *
 * IMPORTANT: The caller is responsible for revoking the blob URL when done
 * using URL.revokeObjectURL() to prevent memory leaks.
 *
 * @param token - The BigCommerce signed_payload_jwt
 * @param imageUrl - The image URL (relative or absolute)
 * @returns A blob URL that can be used as an img src
 * @throws Error if the fetch fails
 *
 * @example
 * ```tsx
 * const blobUrl = await fetchAdminImage(token, '/api/admin/images/att_xyz');
 * // Use in img tag: <img src={blobUrl} />
 * // Clean up when done: URL.revokeObjectURL(blobUrl);
 * ```
 */
export async function fetchAdminImage(token: string, imageUrl: string): Promise<string> {
  const absoluteUrl = getAbsoluteApiUrl(imageUrl);

  const response = await fetch(absoluteUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export const adminApi = {
  questions: {
    /**
     * List questions with optional filtering and pagination.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param params - Optional query parameters (status, page, limit, search, sortBy, sortOrder)
     * @returns Questions list with stats and pagination
     */
    async list(
      token: string,
      params?: AdminQuestionFilters
    ): Promise<ApiResponse<AdminQuestionListResponse>> {
      // Build query string
      const queryParams = new URLSearchParams();

      if (params?.status) {
        queryParams.set('status', params.status);
      }
      if (params?.page !== undefined) {
        queryParams.set('page', String(params.page));
      }
      if (params?.limit !== undefined) {
        queryParams.set('limit', String(params.limit));
      }

      if (params?.search) {
        queryParams.set('search', params.search);
      }
      if (params?.unanswered) {
        queryParams.set('unanswered', 'true');
      }
      if (params?.hasImages) {
        queryParams.set('hasImages', 'true');
      }
      if (params?.needsReview) {
        queryParams.set('needsReview', 'true');
      }
      if (params?.pendingImages) {
        queryParams.set('pendingImages', 'true');
      }

      // P1-10: Add sort parameters
      if (params?.sortBy) {
        queryParams.set('sortBy', params.sortBy);
      }
      if (params?.sortOrder) {
        queryParams.set('sortOrder', params.sortOrder);
      }

      const queryString = queryParams.toString();
      const endpoint = `/api/admin/questions${queryString ? `?${queryString}` : ''}`;

      return apiRequest<AdminQuestionListResponse>(token, endpoint);
    },

    /**
     * Get a single question by ID.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID (format: q_xxxxxxxxxxxx)
     * @returns The question details with images wrapped in `question` property
     */
    async getById(
      token: string,
      questionId: string
    ): Promise<ApiResponse<AdminQuestionDetailResponse>> {
      return apiRequest<AdminQuestionDetailResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}`
      );
    },

    /**
     * Update a question's status.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID (format: q_xxxxxxxxxxxx)
     * @param status - The new status (e.g., 'OPEN', 'CLOSED_REJECTED')
     * @returns The updated question info with confirmation message
     */
    async updateStatus(
      token: string,
      questionId: string,
      status: QuestionStatus,
      /**
       * ADR-025 A-Q1 (OD-19): REQUIRED when status = 'CLOSED_REJECTED' —
       * controlled reason code + optional note. Body is rebuilt
       * field-by-field (the server strict-rejects unknown keys).
       */
      options?: {
        /** ADR-027 D-3: status observed when this operator loaded the question. */
        expectedStatus?: QuestionStatus;
        rejectionReason?: QuestionRejectionReason;
        rejectionNote?: string;
      }
    ): Promise<ApiResponse<UpdateQuestionResponse>> {
      return apiRequest<UpdateQuestionResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            status,
            ...(options?.expectedStatus
              ? { expectedState: { status: options.expectedStatus } }
              : {}),
            ...(options?.rejectionReason ? { rejectionReason: options.rejectionReason } : {}),
            ...(options?.rejectionNote ? { rejectionNote: options.rejectionNote } : {}),
          }),
          // ADR-027 D-12: a guarded mutation has no client idempotency key.
          // Replaying an ambiguous commit would erase the operator's signal and
          // may duplicate downstream side effects.
          maxRetries: 0,
          // A fail-loud audit or later sub-operation can return 5xx after the
          // status prefix committed. Keep that JSON `subOps` report intact so
          // the UI can state what landed and never resend the status.
          preserveFinalRetryableResponse: true,
        }
      );
    },
  },

  notifications: {
    /**
     * Get pending notification counts (ADR-025 A-W pending-work contract).
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @returns Pending-work counts (pendingQuestions, questionsWithPendingReplies,
     *   pendingMedia, deprecated unansweredRfi) + asOf + per-count
     *   oldestItemAgeSeconds (null on a zero count)
     */
    async getPending(token: string): Promise<ApiResponse<PendingNotificationsResponse>> {
      return apiRequest<PendingNotificationsResponse>(token, '/api/admin/notifications/pending');
    },
  },

  launchStatus: {
    /** Minimal capability-gated projection for the persistent shell chip. */
    async get(token: string): Promise<ApiResponse<AdminLaunchStatusResponse>> {
      return apiRequest<AdminLaunchStatusResponse>(token, '/api/admin/launch-status');
    },
  },
  support: {
    /**
     * Read-only, audited, exact-email participant support lookup
     * (BL-ADMIN-PARTICIPANT-SUPPORT-LOOKUP-001).
     *
     * POST (not GET) so the email is never placed in a query string / URL.
     * Returns the participant's notification-subscription state only — no linked
     * questions/replies/queue rows. Every successful lookup is audit-logged
     * server-side.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param email - Exact participant email to resolve
     * @returns The participant's subscription state
     */
    async participantLookup(
      token: string,
      email: string
    ): Promise<ApiResponse<ParticipantSupportLookupResponse>> {
      return apiRequest<ParticipantSupportLookupResponse>(
        token,
        '/api/admin/support/participant-lookup',
        {
          method: 'POST',
          body: JSON.stringify({ email }),
        }
      );
    },

    /**
     * Audited, consent-gated participant notification re-subscribe
     * (BL-ADMIN-NOTIFICATION-RESUBSCRIBE-001).
     *
     * Re-enables a globally-unsubscribed participant's notifications. v1 is an
     * audited admin re-enable with NO email sent (double-opt-in deferred). POST
     * (not GET) so the email is never placed in a query string / URL. Requires a
     * non-empty reason and an explicit documented-recipient-request affirmation;
     * every successful re-enable is audit-logged server-side. Returns the
     * resulting subscription state only — no raw email/hash, no linked rows.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param params - Exact email, operator reason, and the required affirmation
     * @returns The participant's resulting subscription state
     */
    async participantResubscribe(
      token: string,
      params: { email: string; reason: string }
    ): Promise<ApiResponse<ParticipantResubscribeResponse>> {
      return apiRequest<ParticipantResubscribeResponse>(
        token,
        '/api/admin/support/participant-resubscribe',
        {
          method: 'POST',
          body: JSON.stringify({
            email: params.email,
            reason: params.reason,
            userRequestConfirmed: true,
          }),
        }
      );
    },

    /**
     * Read-only, audited consent-record inspection
     * (BL-PRIVACY-CONSENT-CAPTURE-001).
     *
     * Resolves an exact `questionId` to the public-display consent records
     * captured for that question and its replies (version / textKey / grantedAt
     * / locale + whether an admin-only IP was captured). POST (not GET) keeps
     * the id out of any access log; every inspection is audit-logged
     * server-side. The raw IP is never returned — only its availability.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - Exact question id to inspect
     * @returns The consent-record projection for the question + replies
     */
    async consentInspection(
      token: string,
      questionId: string
    ): Promise<ApiResponse<ConsentInspectionResponse>> {
      return apiRequest<ConsentInspectionResponse>(token, '/api/admin/support/consent-inspection', {
        method: 'POST',
        body: JSON.stringify({ questionId }),
      });
    },
  },

  settings: {
    /**
     * Get current app settings.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @returns Current store settings
     */
    async get(token: string): Promise<ApiResponse<StoreSettingsResponse>> {
      return apiRequest<StoreSettingsResponse>(token, '/api/admin/settings');
    },

    /**
     * Update app settings.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param settings - Partial settings to update
     * @returns Updated settings or error
     */
    async update(
      token: string,
      settings: UpdateStoreSettingsRequest
    ): Promise<ApiResponse<StoreSettingsResponse>> {
      return apiRequest<StoreSettingsResponse>(token, '/api/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(settings),
        // Guarded writes have no idempotency key. A retry after an ambiguous
        // commit can duplicate side effects and erase the operator's signal.
        maxRetries: 0,
      });
    },
  },

  replies: {
    /**
     * List all replies for a question (admin moderation view).
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param params - Optional filters (status, page, limit)
     * @returns Paginated list of replies
     */
    async list(
      token: string,
      questionId: string,
      params?: AdminReplyFilters
    ): Promise<ApiResponse<AdminReplyListResponse>> {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.set('status', params.status);
      if (params?.page !== undefined) queryParams.set('page', String(params.page));
      if (params?.limit !== undefined) queryParams.set('limit', String(params.limit));

      const queryString = queryParams.toString();
      const endpoint = `/api/admin/questions/${encodeURIComponent(questionId)}/replies${queryString ? `?${queryString}` : ''}`;

      return apiRequest<AdminReplyListResponse>(token, endpoint);
    },

    /**
     * Moderate a reply (approve/reject/hide).
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param replyId - The reply ID
     * @param action - The moderation action
     * @param reason - Required for REJECT, optional for HIDE
     * @returns Updated reply state and side effects
     */
    async moderate(
      token: string,
      questionId: string,
      replyId: string,
      action: ReplyModerationAction,
      reason?: string
    ): Promise<ApiResponse<ModerateReplyResponse>> {
      return apiRequest<ModerateReplyResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/replies/${encodeURIComponent(replyId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ action, reason } satisfies ModerateReplyRequest),
        }
      );
    },

    /**
     * Create a staff-authored (admin) reply for a question.
     *
     * Activates the PR #315 backend route `POST /api/admin/questions/:id/replies`.
     * The request contract is intentionally narrow: the ONLY field sent is
     * `body`. Every authority-bearing field (userType, author identity,
     * replyType, isSolution, moderationStatus, capability, store hash) is
     * SERVER-DERIVED — nothing here can assert them. The payload is structurally
     * constrained to `CreateStaffReplyRequest` and only that constrained object
     * is serialized, so no spoofable field can leak into the request body.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param body - Plain-text reply body (validated server-side)
     * @returns The narrow, display-safe staff-reply response
     */
    async createStaffReply(
      token: string,
      questionId: string,
      body: string
    ): Promise<ApiResponse<CreateStaffReplyResponse>> {
      const requestBody = { body } satisfies CreateStaffReplyRequest;
      return apiRequest<CreateStaffReplyResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/replies`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody),
        }
      );
    },

    /**
     * Mark a reply as the solution for a question.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param replyId - The reply ID
     * @returns Updated solution state
     */
    async markSolution(
      token: string,
      questionId: string,
      replyId: string,
      request?: MarkSolutionRequest
    ): Promise<
      ApiResponse<{
        replyId: string;
        questionId: string;
        isSolution: boolean;
        message: string;
      }>
    > {
      return apiRequest(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/replies/${encodeURIComponent(replyId)}/solution`,
        {
          method: 'PATCH',
          ...(request?.expectedState
            ? {
                body: JSON.stringify({
                  expectedState: { status: request.expectedState.status },
                } satisfies MarkSolutionRequest),
              }
            : {}),
          // A solution mutation can commit before a later audit/side effect
          // failure becomes visible. Never replay an ambiguous request.
          maxRetries: 0,
          preserveFinalRetryableResponse: true,
        }
      );
    },

    /**
     * Unmark a reply as the solution.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param replyId - The reply ID
     * @returns Updated solution state
     */
    async unmarkSolution(
      token: string,
      questionId: string,
      replyId: string
    ): Promise<
      ApiResponse<{
        replyId: string;
        questionId: string;
        isSolution: boolean;
        message: string;
      }>
    > {
      return apiRequest(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/replies/${encodeURIComponent(replyId)}/solution`,
        {
          method: 'DELETE',
          // Tokenless by design: the server-held RESOLVED/SOLUTION_MARKED
          // predicate is this route's precondition. Do not put a body on DELETE.
          maxRetries: 0,
          preserveFinalRetryableResponse: true,
        }
      );
    },
  },

  /**
   * RFI (Request-For-Information) management — the staff/admin workflow overlay
   * on replies (ADR-020 §5 / §5.2; backend PR A–F). Every method is behind the
   * worker's `RFI_MANAGE` capability and tenant-scoped server-side; the UI never
   * sends actor/store/identity fields (those are server-derived and rejected if
   * present). Outbound bodies are rebuilt field-by-field from the typed `body`
   * so even a widened object can't leak extra keys over the wire (`satisfies` is
   * a compile-time check, NOT runtime stripping).
   *
   * Return-shape asymmetry (typed precisely): `create` and `reviewItem` carry a
   * `statusTransition`; `linkResponses`/`unlinkResponse` are status-neutral and
   * return only `{ rfi }`. `CreateRfiResponse.message` is the server success
   * message (a `string`), NOT the staff RFI prose.
   */
  rfi: {
    /**
     * Open an official RFI request on a question.
     *
     * `maxRetries: 0` — opening an RFI can commit both the request and its
     * question-status transition before a response is lost, so retrying from
     * the client could duplicate a partially successful mutation.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param body - `{ message, items }` (allow-listed; items rebuilt field-by-field)
     * @returns The created RFI + server message + open-side status transition
     */
    async create(
      token: string,
      questionId: string,
      body: CreateRfiRequest
    ): Promise<ApiResponse<CreateRfiResponse>> {
      // Reconstruct the body field-by-field from the typed `body` — never spread
      // form state. `unit` is sent only for measurement items (and only when
      // non-empty); `options` only for selection items.
      const requestBody = {
        message: body.message,
        items: body.items.map((item) => ({
          itemType: item.itemType,
          label: item.label,
          required: item.required,
          ...(item.itemType === 'measurement' && item.unit ? { unit: item.unit } : {}),
          ...(item.itemType === 'selection' ? { options: item.options } : {}),
        })),
      } satisfies CreateRfiRequest;
      return apiRequest<CreateRfiResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/rfi`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody),
          maxRetries: 0,
        }
      );
    },

    /**
     * List the official RFI requests (with ordered items + statuses) for a
     * question. Returns `{ rfis: [] }` (200) — never 404 — when there are none.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @returns The question's RFI requests (createdAt ASC, server-ordered)
     */
    async list(token: string, questionId: string): Promise<ApiResponse<AdminRfiListResponse>> {
      return apiRequest<AdminRfiListResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/rfi`
      );
    },

    /**
     * Link one APPROVED, non-deleted, GENERAL reply to one or more RFI items.
     * Status-neutral — returns only the updated `{ rfi }`.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param rfiId - The RFI ID
     * @param body - `{ replyId, links }` (each target rebuilt field-by-field)
     * @returns The updated RFI
     */
    async linkResponses(
      token: string,
      questionId: string,
      rfiId: string,
      body: CreateRfiResponseLinkInput
    ): Promise<ApiResponse<{ rfi: AdminRfiRequest }>> {
      // Rebuild each link target field-by-field: trim `valueText` and OMIT it if
      // empty (an empty string 400s); include `attachmentId` only when set.
      const requestBody = {
        replyId: body.replyId,
        links: body.links.map((link) => {
          const target: RfiResponseLinkTarget = { rfiItemId: link.rfiItemId };
          if (link.attachmentId) {
            target.attachmentId = link.attachmentId;
          }
          const trimmedValueText = link.valueText?.trim();
          if (trimmedValueText) {
            target.valueText = trimmedValueText;
          }
          return target;
        }),
      } satisfies CreateRfiResponseLinkInput;
      return apiRequest<{ rfi: AdminRfiRequest }>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/rfi/${encodeURIComponent(rfiId)}/responses`,
        {
          method: 'POST',
          body: JSON.stringify(requestBody),
        }
      );
    },

    /**
     * Unlink a single response. Status-neutral — returns only the updated
     * `{ rfi }`. The worker rejects unlinking the last response from a staff-zone
     * item (409 + "reset its review to UNREVIEWED first").
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param rfiId - The RFI ID
     * @param rfiResponseId - The response-link ID to remove
     * @returns The updated RFI
     */
    async unlinkResponse(
      token: string,
      questionId: string,
      rfiId: string,
      rfiResponseId: string
    ): Promise<ApiResponse<{ rfi: AdminRfiRequest }>> {
      return apiRequest<{ rfi: AdminRfiRequest }>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/rfi/${encodeURIComponent(rfiId)}/responses/${encodeURIComponent(rfiResponseId)}`,
        { method: 'DELETE' }
      );
    },

    /**
     * Set/reset an item's review status. `reviewStatus` ∈ {UNREVIEWED,
     * INSUFFICIENT, PARTIAL, COMPLETE} — `OPEN` is system-derived and rejected.
     * Requires ≥1 linked response (else 409). Carries a completion-side
     * `statusTransition`.
     *
     * `maxRetries: 0` — review can commit before its completion-side status
     * transition response is lost, so an automatic retry could replay a
     * partially successful mutation.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param rfiId - The RFI ID
     * @param rfiItemId - The RFI item ID
     * @param body - `{ reviewStatus }`
     * @returns The updated RFI + completion-side status transition
     */
    async reviewItem(
      token: string,
      questionId: string,
      rfiId: string,
      rfiItemId: string,
      body: ReviewRfiItemInput
    ): Promise<ApiResponse<ReviewRfiItemResponse>> {
      const requestBody = {
        reviewStatus: body.reviewStatus,
      } satisfies ReviewRfiItemInput;
      return apiRequest<ReviewRfiItemResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/rfi/${encodeURIComponent(rfiId)}/items/${encodeURIComponent(rfiItemId)}/review`,
        {
          method: 'PATCH',
          body: JSON.stringify(requestBody),
          maxRetries: 0,
        }
      );
    },

    /**
     * Cancel (withdraw) an official RFI request — a TERMINAL transition (PR H).
     * Carries a cancel-side `statusTransition` (`CancelRfiResponse`).
     *
     * `maxRetries: 0` — this is a destructive, non-idempotent-from-the-client
     * action: the default retry would, after a silently-successful commit, hit the
     * sequential-re-cancel 409 and surface a FALSE error. Sends NO body when there
     * is no `reasonCode` (never `JSON.stringify(undefined)`/`{}`), exercising the
     * route's no-body path; the v1 UI passes none.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID
     * @param rfiId - The RFI ID
     * @param body - Optional `{ reasonCode }`
     * @returns The cancelled RFI + cancel-side status transition
     */
    async cancel(
      token: string,
      questionId: string,
      rfiId: string,
      body?: CancelRfiRequest
    ): Promise<ApiResponse<CancelRfiResponse>> {
      const options: ApiRequestOptions = { method: 'POST', maxRetries: 0 };
      if (body?.reasonCode) {
        options.body = JSON.stringify({ reasonCode: body.reasonCode });
      }
      return apiRequest<CancelRfiResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/rfi/${encodeURIComponent(rfiId)}/cancel`,
        options
      );
    },
  },

  analytics: {
    /**
     * Get comprehensive analytics data for the admin dashboard.
     *
     * PHASE_0C_SPEC.md: Task 0C-36
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @returns Analytics data including 7-day trends, response times, and status distribution
     */
    async get(token: string): Promise<ApiResponse<AnalyticsResponse>> {
      return apiRequest<AnalyticsResponse>(token, '/api/admin/analytics');
    },
  },

  tags: {
    /**
     * Get tag suggestions for question content.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param query - Text to analyze for tag suggestions
     * @param limit - Maximum tags to return (default: 5, max: 10)
     * @returns Suggested tags and top tags
     */
    async suggest(
      token: string,
      query: string,
      limit: number = 5
    ): Promise<ApiResponse<TagSuggestionResponse>> {
      const params = new URLSearchParams({ q: query });
      if (limit !== 5) params.set('limit', String(limit));
      return apiRequest<TagSuggestionResponse>(
        token,
        `/api/admin/tags/suggest?${params.toString()}`
      );
    },

    /**
     * Get the full tag taxonomy for admin UI.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @returns Taxonomy categories and allowed tags
     */
    async taxonomy(token: string): Promise<ApiResponse<AdminTagTaxonomyResponse>> {
      return apiRequest<AdminTagTaxonomyResponse>(token, '/api/admin/tags/taxonomy');
    },
  },

  reputation: {
    /**
     * List all user reputation records.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param params - Pagination options (page, limit)
     * @returns Paginated list of user reputation records
     */
    async get(
      token: string,
      params?: { page?: number; limit?: number }
    ): Promise<ApiResponse<AdminReputationListResponse>> {
      const queryParams = new URLSearchParams();
      if (params?.page !== undefined) queryParams.set('page', String(params.page));
      if (params?.limit !== undefined) queryParams.set('limit', String(params.limit));
      const queryString = queryParams.toString();
      const endpoint = `/api/admin/reputation${queryString ? `?${queryString}` : ''}`;
      return apiRequest<AdminReputationListResponse>(token, endpoint);
    },

    /**
     * Get top users by reputation points.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param limit - Number of top users to return (default: 10, max: 100)
     * @returns Leaderboard of top users
     */
    async leaderboard(token: string, limit?: number): Promise<ApiResponse<LeaderboardResponse>> {
      const queryParams = new URLSearchParams();
      if (limit !== undefined) queryParams.set('limit', String(limit));
      const queryString = queryParams.toString();
      const endpoint = `/api/admin/reputation/leaderboard${queryString ? `?${queryString}` : ''}`;
      return apiRequest<LeaderboardResponse>(token, endpoint);
    },
  },

  products: {
    /**
     * Search BigCommerce products for linking to questions.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param query - Search query (product name, SKU, etc.)
     * @param limit - Maximum results to return (default: 10, max: 50)
     * @returns Matching products
     */
    async search(
      token: string,
      query: string,
      limit?: number
    ): Promise<ApiResponse<ProductSearchResponse>> {
      const params = new URLSearchParams({ q: query });
      if (limit !== undefined) params.set('limit', String(limit));
      return apiRequest<ProductSearchResponse>(
        token,
        `/api/admin/products/search?${params.toString()}`
      );
    },

    /**
     * Get multiple products by their IDs.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param productIds - Array of BigCommerce product IDs
     * @returns Products matching the given IDs
     */
    async getByIds(
      token: string,
      productIds: number[]
    ): Promise<ApiResponse<ProductsByIdsResponse>> {
      if (productIds.length === 0) {
        return { success: true, data: { products: [] } };
      }
      const idsParam = productIds.map(String).join(',');
      return apiRequest<ProductsByIdsResponse>(
        token,
        `/api/admin/products/${encodeURIComponent(idsParam)}`
      );
    },
  },

  audit: {
    /**
     * List admin audit-trail entries (PR I-2 — the read surface from ADR-023).
     *
     * `GET /api/admin/audit-logs` — returns display-safe `AuditLogView[]` plus a
     * keyset `nextCursor`. Pass `cursor` (from a previous response's
     * `nextCursor`) to load the next, older page; `null` means the end of the
     * trail. `fromDate`/`toDate` are full ISO-8601 datetimes (inclusive).
     *
     * The optional `signal` lets the caller cancel a superseded request; a
     * caller-initiated abort rejects with a real `AbortError` (NOT an error
     * envelope), so `useAudit` can detect and silently ignore it.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param query - Optional filters + keyset pagination (action / entityType /
     *   entityId / fromDate / toDate / limit / cursor)
     * @param signal - Optional AbortController signal for cancellation
     * @returns Display-safe audit log page + `nextCursor`
     */
    async list(
      token: string,
      query: AuditLogListQuery = {},
      signal?: AbortSignal
    ): Promise<ApiResponse<AuditLogListResponse>> {
      const queryParams = new URLSearchParams();
      if (query.action) queryParams.set('action', query.action);
      if (query.entityType) queryParams.set('entityType', query.entityType);
      if (query.entityId) queryParams.set('entityId', query.entityId);
      if (query.fromDate) queryParams.set('fromDate', query.fromDate);
      if (query.toDate) queryParams.set('toDate', query.toDate);
      if (query.limit !== undefined) queryParams.set('limit', String(query.limit));
      if (query.cursor) queryParams.set('cursor', query.cursor);

      const queryString = queryParams.toString();
      const endpoint = `/api/admin/audit-logs${queryString ? `?${queryString}` : ''}`;

      return apiRequest<AuditLogListResponse>(token, endpoint, { signal });
    },
  },

  media: {
    /**
     * List the media moderation review queue (ADR-022 §10(D) —
     * BL-IMG-ADMIN-MODERATION-001).
     *
     * `GET /api/admin/media` — MEDIA_MODERATE-gated; returns admin-only
     * `AdminMediaListItem[]` (includes non-APPROVED rows, rejectionReason,
     * legalHold) plus offset pagination and store-wide per-status counts.
     * Item `url` is the AUTHENTICATED admin byte route — render through
     * `AdminImageThumbnail` (blob fetch), never a raw `<img src>`.
     *
     * `anchor: 'question'` (fold C1) restricts results to QUESTION-anchored
     * rows (`reply_id IS NULL`) — the question-gallery management scope;
     * omitted, reply-anchored rows are included (moderation-queue scope).
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param params - Optional status/questionId/anchor filters + page/limit
     * @param signal - Optional AbortController signal for cancellation
     */
    async list(
      token: string,
      params?: AdminMediaFilters,
      signal?: AbortSignal
    ): Promise<ApiResponse<AdminMediaListResponse>> {
      const queryParams = new URLSearchParams();
      if (params?.status) queryParams.set('status', params.status);
      if (params?.questionId) queryParams.set('questionId', params.questionId);
      if (params?.anchor) queryParams.set('anchor', params.anchor);
      if (params?.page !== undefined) queryParams.set('page', String(params.page));
      if (params?.limit !== undefined) queryParams.set('limit', String(params.limit));

      const queryString = queryParams.toString();
      const endpoint = `/api/admin/media${queryString ? `?${queryString}` : ''}`;

      return apiRequest<AdminMediaListResponse>(token, endpoint, { signal });
    },

    /**
     * Apply a moderation transition to a media attachment.
     *
     * `PATCH /api/admin/media/:attachmentId` — action APPROVE | REJECT |
     * HIDE | SHOW (SHOW = the guarded HIDDEN → APPROVED recovery, PR4b —
     * vetoed with a 409 + `details.legalHold` on a held row); REJECT requires
     * a reason from the controlled `MediaRejectionReason` vocabulary (no free
     * text). REJECT with SUSPECTED_ILLEGAL is remapped server-side to HIDDEN
     * + legal hold (D-LEGALHOLD).
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param attachmentId - The attachment ID (format: att_xxxxxxxxxxxx)
     * @param action - The moderation action
     * @param reason - Required for REJECT (controlled vocabulary)
     */
    async moderate(
      token: string,
      attachmentId: string,
      action: MediaModerationAction,
      reason?: MediaRejectionReason
    ): Promise<ApiResponse<ModerateMediaResponse>> {
      return apiRequest<ModerateMediaResponse>(
        token,
        `/api/admin/media/${encodeURIComponent(attachmentId)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({ action, reason } satisfies ModerateMediaRequest),
        }
      );
    },

    /**
     * Apply ONE moderation action to a bounded set of attachments with
     * per-item results (`POST /api/admin/media/bulk`). Not retried
     * (maxRetries 0): a retry of a partially-applied bulk would re-run
     * items whose first attempt already applied.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param body - attachmentIds (≤ MEDIA_MODERATION_BULK_MAX), action, reason?
     */
    async moderateBulk(
      token: string,
      body: BulkModerateMediaRequest
    ): Promise<ApiResponse<BulkModerateMediaResponse>> {
      return apiRequest<BulkModerateMediaResponse>(token, '/api/admin/media/bulk', {
        method: 'POST',
        body: JSON.stringify(body),
        maxRetries: 0,
      });
    },

    /**
     * Staff question-management image add (ADR-022 §10(A)/(B) "Add = upload" —
     * PR4b, BL-IMG-ADMIN-AUTHORING-001).
     *
     * `POST /api/admin/questions/:questionId/images` — MEDIA_AUTHOR-gated
     * multipart upload through the SAME server pipeline as the public paths.
     * The body carries ONLY the file: author type/identity, auto-APPROVED
     * moderation status, and the §10.1-resolved caps are all server-derived —
     * nothing here can assert them. Not retried (`maxRetries: 0` — see the
     * multipart notes on `apiRequest`, R1 fold).
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param questionId - The question ID (format: q_xxxxxxxxxxxx)
     * @param file - The image file to upload
     */
    async uploadQuestionImage(
      token: string,
      questionId: string,
      file: File
    ): Promise<ApiResponse<AdminUploadImageResponse>> {
      const formData = new FormData();
      formData.append('file', file);
      return apiRequest<AdminUploadImageResponse>(
        token,
        `/api/admin/questions/${encodeURIComponent(questionId)}/images`,
        { method: 'POST', body: formData, maxRetries: 0 }
      );
    },

    /**
     * Staff reply / RFI-anchor image authoring (ADR-022 §10(A) — PR4b).
     *
     * `POST /api/admin/replies/:replyId/images` — same contract as
     * `uploadQuestionImage`; the server additionally enforces the §10(A)
     * anchor restriction (the reply must be STAFF-authored — a staff GENERAL
     * reply or an RFI_REQUEST anchor; customer replies 403).
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param replyId - The staff-authored reply ID (format: r_xxxxxxxxxxxx)
     * @param file - The image file to upload
     */
    async uploadReplyImage(
      token: string,
      replyId: string,
      file: File
    ): Promise<ApiResponse<AdminUploadImageResponse>> {
      const formData = new FormData();
      formData.append('file', file);
      return apiRequest<AdminUploadImageResponse>(
        token,
        `/api/admin/replies/${encodeURIComponent(replyId)}/images`,
        { method: 'POST', body: formData, maxRetries: 0 }
      );
    },

    /**
     * Admin remove (ADR-022 §10(B) — PR4b): SOFT-delete + version bump;
     * stored bytes are retained until the purge executor. Legal-held rows
     * 409 with `details.legalHold: true` (D-LEGALHOLD — held evidence is
     * never deletable).
     *
     * `maxRetries: 0` — destructive, non-idempotent-from-the-client action
     * (rfi.cancel precedent): the default retry would, after a silently
     * successful commit, hit the sequential re-delete MEDIA_NOT_FOUND 404
     * and surface a FALSE error.
     *
     * @param token - The BigCommerce signed_payload_jwt
     * @param attachmentId - The attachment ID (format: att_xxxxxxxxxxxx)
     */
    async remove(
      token: string,
      attachmentId: string
    ): Promise<ApiResponse<AdminDeleteMediaResponse>> {
      return apiRequest<AdminDeleteMediaResponse>(
        token,
        `/api/admin/media/${encodeURIComponent(attachmentId)}`,
        { method: 'DELETE', maxRetries: 0 }
      );
    },
  },
};
